import React from 'react'

import { Table } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import {
  TimeInsightsInconsistencySubscriptionDocument,
  TimesheetEstimatedTimeFragment,
  TimesheetPageEmployeesSettingFragment,
} from 'generated/resources/graphql'
import { groupBy } from 'lodash'
import moment from 'moment'

import Loading from 'modules/core/components/Loading'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import { dateToString } from 'modules/core/lib/dates'

import type Store from 'modules/attendance/components/ClockIn/Store'
import { InconsistencyState } from 'modules/attendance/components/ClockIn/types'
import { useFetchShiftLocations } from 'modules/attendance/hooks/shift_locations/useFetchShiftLocations'
import { useShiftInconsistencies } from 'modules/attendance/hooks/useShiftInconsistencies'
import useSubscriptionAggregatedBalanceRefreshed from 'modules/attendance/hooks/useSubscriptionAggregatedBalanceRefreshed'
import useSubscriptionBalanceRefreshed from 'modules/attendance/hooks/useSubscriptionBalanceRefreshed'
import useSubscriptionWorkedTimeRefreshed from 'modules/attendance/hooks/useSubscriptionWorkedTimeRefreshed'
import { useAttendanceBreaks } from 'modules/attendance/lib/attendanceBreaksContext'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { OVERNIGHT_SHIFTS_CALCULATION_METHODS } from 'modules/attendance/lib/enums'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import { PopoverTypeToShow } from 'modules/attendance/lib/types'
import AttendanceShiftUtils from 'modules/attendance/lib/utils/AttendanceShift'
import BalanceUtils from 'modules/attendance/lib/utils/Balance'
import CompanySettingsVersionHistoriesUtils from 'modules/attendance/lib/utils/CompanySettingsVersionHistories'
import EmployeesSettingsUtils from 'modules/attendance/lib/utils/EmployeesSettingsUtils'
import EstimatedTimesUtils from 'modules/attendance/lib/utils/EstimatedTimes'

import DayRow from './DayRow'
import PeriodStatus from './PeriodStatus'
import TableHeader from './TableHeader'

interface Props {
  store: Store
  showHourBalance: boolean
}

// to update the useToggle from core and test it
type LazyToggleMethod = (value: boolean) => () => void
type Return = [boolean, LazyToggleMethod]

const useToggle = (startingValue: boolean): Return => {
  const [value, setValue] = React.useState(startingValue)
  const lazyToggleFunction = (newValue: boolean) => () => setValue(newValue)

  return [value, lazyToggleFunction]
}

const HourlyTable = ({ store, showHourBalance }: Props) => {
  const { allBreaks, attendanceBreaks, loadingBreaks } = useAttendanceBreaks()
  const { stringDateFrom: startOn, stringDateTo: endOn } = useAttendanceDateNavigator()
  const { featureAvailability: hasGeofencingFeature } = useAttendanceFeatures(
    Features.time_tracking_geofencing_alerts
  )
  const { featureAvailability: hasCorrectiveBreaks } = useAttendanceFeatures(
    'time_tracking_corrective_breaks'
  )
  const { featureAvailability: hasExtraHourManagementV2 } = useAttendanceFeatures(
    'dev_time_tracking_extra_hour_management_v2'
  )
  const [expandAll, toggleExpandAll] = useToggle(false)
  const { employeeId } = store

  const {
    workedBreaks,
    balances,
    companySettingsVersionHistories,
    estimatedTimes,
    workedTimes,
    editTimesheetRequests,
    shifts,
    reviewsStats,
    employeesSettings,
    attendanceEmployeesPolicyId,
    isPartTime,
    featureByName,
    currentEmployeesSetting,
  } = useTimesheetData()

  const hasGeolocationEnabled = EmployeesSettingsUtils.hasGeoLocation(employeesSettings)

  const { shiftLocations } = useFetchShiftLocations({
    dateFrom: startOn,
    dateTo: endOn,
    employeeIds: [employeeId],
    skip: !hasGeofencingFeature && !hasGeolocationEnabled,
  })

  const { inconsistencies, refetchInconsistencies } = useShiftInconsistencies({
    dataType: PopoverTypeToShow.Info,
    employeeIds: [employeeId],
    startOn,
    endOn,
    state: InconsistencyState.pending,
  })

  const overnightShiftsCalculationMethodsByDates =
    CompanySettingsVersionHistoriesUtils.overnightShiftsCalculationMethodsByDates(
      companySettingsVersionHistories
    )

  const estimatedTimesByDates = EstimatedTimesUtils.estimatedTimesByDates(estimatedTimes)

  const groupedDateBalance = groupBy(balances, ({ date }) => date)
  const groupedDateWorkedTime = groupBy(workedTimes, ({ date }) => date)

  const groupedBreaksByDate = groupBy(workedBreaks, 'date')
  const groupedEditTimesheetRequestsDate = groupBy(editTimesheetRequests, 'date')

  const momentDays = AttendanceShiftUtils.daysBetweenDates(moment(startOn), moment(endOn))

  useSubscriptionWorkedTimeRefreshed({ employeeId: employeeId, startOn, endOn })
  useSubscriptionAggregatedBalanceRefreshed({ employeeId: employeeId })
  useSubscriptionBalanceRefreshed({ employeeId: employeeId })

  useSubscription(TimeInsightsInconsistencySubscriptionDocument, {
    variables: {},
    onData: async ({ data }) => {
      if (data?.data?.timeinsightsInconsistencyCreate?.inconsistency?.id) {
        await refetchInconsistencies()
      }
    },
  })

  const hasDailyExtraHoursTransferFrequency =
    employeesSettings.find(
      (setting) => setting.attendanceEmployeesPolicy.id === attendanceEmployeesPolicyId
    )?.extraHoursTransferFrequency === 'daily'

  const showExtraHoursColumn = hasDailyExtraHoursTransferFrequency && hasExtraHourManagementV2

  const hasComplementaryHoursFeature = TimesheetFeatures.COMPLEMENTARY_HOURS in featureByName
  const showComplementaryHoursColumn =
    hasComplementaryHoursFeature &&
    isPartTime &&
    hasDailyExtraHoursTransferFrequency &&
    hasExtraHourManagementV2 &&
    !!currentEmployeesSetting?.enableComplementaryHours

  const days = momentDays.map((day) => {
    const calendarDay = store.getDay(day.date())

    if (!calendarDay) return null

    const attendanceSetting =
      EmployeesSettingsUtils.getSettingWithinDate<TimesheetPageEmployeesSettingFragment>(
        employeesSettings,
        day,
        attendanceEmployeesPolicyId
      )

    if (!attendanceSetting) return <></>

    const dateString = dateToString(day, 'backend')

    const balanceMinutes = BalanceUtils.roundedBalanceMinutes(
      groupedDateBalance[dateString]?.[0]?.dailyBalanceFromPlanning
    )
    const dayEditTimesheetRequests = groupedEditTimesheetRequestsDate[dateString] ?? []
    const dayShiftLocations = shiftLocations?.filter(
      (shiftLocation) => shiftLocation.date === dateString
    )
    const dateShifts = shifts.filter((shift) => shift.referenceDate === dateString)

    const estimatedTime =
      estimatedTimesByDates[dateString] ??
      EstimatedTimesUtils.empty<TimesheetEstimatedTimeFragment>(dateString)

    const firstGroupedDateWorkedTime = groupedDateWorkedTime[dateString]?.[0]
    const trackedMinutes = firstGroupedDateWorkedTime?.trackedMinutes ?? 0
    const workedMinutes = firstGroupedDateWorkedTime?.minutes ?? 0
    const effectiveWorkedMinutes = firstGroupedDateWorkedTime?.effectiveWorkedMinutes ?? 0
    const workedMinutesNotApproved = firstGroupedDateWorkedTime?.pendingMinutes ?? 0
    const breaksMinutesRounded = firstGroupedDateWorkedTime?.breaksMinutesRounded ?? 0

    const dayInconsistencies = inconsistencies?.has(dateString)
      ? inconsistencies?.get(dateString)
      : undefined
    const showStartsNextDayCheckbox =
      overnightShiftsCalculationMethodsByDates[dateString] ===
      OVERNIGHT_SHIFTS_CALCULATION_METHODS.beginning_shift

    return (
      <DayRow
        shiftLocations={dayShiftLocations}
        key={day.format()}
        date={day}
        store={store}
        calendarDay={calendarDay}
        showHourBalance={showHourBalance}
        dayBreaks={attendanceBreaks?.get(day.format('YYYY-MM-DD')) ?? []}
        allBreaks={allBreaks}
        workedBreaks={groupedBreaksByDate}
        expandAll={expandAll}
        inconsistencies={dayInconsistencies}
        showStartsNextDayCheckbox={showStartsNextDayCheckbox}
        balanceMinutes={balanceMinutes}
        attendanceSetting={attendanceSetting}
        estimatedTime={estimatedTime}
        trackedMinutes={trackedMinutes}
        workedMinutes={workedMinutes}
        effectiveWorkedMinutes={effectiveWorkedMinutes}
        workedMinutesNotApproved={workedMinutesNotApproved}
        breaksMinutesRounded={breaksMinutesRounded}
        hasCorrectiveBreaks={hasCorrectiveBreaks}
        hasExtraHourManagementV2={hasExtraHourManagementV2}
        showExtraHoursColumn={showExtraHoursColumn}
        showComplementaryHoursColumn={showComplementaryHoursColumn}
        editTimesheetRequests={dayEditTimesheetRequests}
        shifts={dateShifts}
      />
    )
  })

  // Scroll to the current date row when the component mounts
  useEffect(() => {
    const currentDateRow = document.querySelector('.current-date-row')
    if (currentDateRow) {
      currentDateRow.scrollIntoView({ block: 'start' })
    }
  }, [])

  return (
    <>
      <PeriodStatus
        reviewsStats={reviewsStats}
        store={store}
        toggleExpandAll={toggleExpandAll}
        expandAll={expandAll}
      />
      <Table noRadius fullWidth autoLayout>
        <TableHeader
          hasCorrectiveBreaks={hasCorrectiveBreaks}
          hasExtraHourManagementV2={hasExtraHourManagementV2}
          showExtraHoursColumn={showExtraHoursColumn}
          showComplementaryHoursColumn={showComplementaryHoursColumn}
        />
        {loadingBreaks && <Loading />}
        <Table.Body>{!loadingBreaks && days}</Table.Body>
      </Table>
    </>
  )
}

export default HourlyTable
